import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '@/shared/services/authService';
import { useAuthStore } from '@/stores/authStore';
import { dashboardKeys } from '@/features/dashboard/hooks/useDashboard';
import { dashboardService } from '@/features/dashboard/services/dashboardService';

export function AuthPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSession, setUser } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { session, user: authUser } = await authService.signIn(email, password);

      if (session && authUser) {
        setSession(session);
        const userProfile = await authService.getUserProfile(authUser.id);
        setUser(userProfile);

        // Prefetch dashboard data before navigation
        const locationIds = userProfile.locations.map((l) => l.id);
        if (locationIds.length > 0) {
          // Prefetch in parallel - don't wait for completion
          Promise.all([
            queryClient.prefetchQuery({
              queryKey: dashboardKeys.alerts(locationIds),
              queryFn: () => dashboardService.getDashboardAlerts(locationIds),
            }),
            queryClient.prefetchQuery({
              queryKey: dashboardKeys.summary(locationIds),
              queryFn: () => dashboardService.getMultiLocationSummary(locationIds),
            }),
          ]).catch((err) => {
            // Silently fail prefetch - dashboard will fetch on load
            console.error('Dashboard prefetch failed:', err);
          });
        }

        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Frame Replenishment System</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
